import * as React from "react";
import { App } from "./app";
import { createBrowserRouter, createRoutesFromElements, Outlet, Route, RouterProvider } from "react-router-dom";
import { ConfigProvider, Spin } from "antd";
import * as Sentry from "@sentry/react";
import { initializeTheme } from "./store/themeSlice";
import {
  DEPLOYMENT_ENV,
  HUBSPOT_SERVICE_URL,
  IS_DEPLOYMENT_ENV_TEST,
  IS_HUBSPOT_INTEGRATION_ENABLED,
  IS_SENTRY_ENABLED,
} from "./envVariables";
import { request } from "./services/cpln";
import { SignIn } from "./components/system/auth/signin";
import { SignUpWithId } from "./components/system/auth/signin/signUpWithId";
import { DocumentTitle } from "./components/documentTitle/DocumentTitle";
import { MediaQueryContext, useInitMediaQueryContext } from "./components/detail/mediaQueryContext";
import { ExternalLink } from "./newcomponents/table/components/ExternalLink";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { initializeUserProvider, selectIsCplnTeam, selectIsLoggedIn, setUser } from "./store/userSlice";
import { initializeFirebase } from "./store/firebase";
import { fetchChargeableItems } from "./store/restSlice";
import { fetchDiscovery } from "./store/discoverySlice";
import { sentryMetadata, setupSentry } from "./tools/sentry";
import { MarketplaceProviderAWS } from "./pages/marketplace-providers/aws/aws";
import { initializeUiData } from "./store/uiDataSlice";

export const EntryPoint: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((store) => store.theme.theme);
  const discoveryRequesting = useAppSelector((state) => state.discovery.requesting);
  const discoveryRequested = useAppSelector((state) => state.discovery.requested);
  const discoveryError = useAppSelector((state) => state.discovery.error);

  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const isProcessing = useAppSelector((state) => state.user.isProcessing);
  const isCplnTeam = useAppSelector(selectIsCplnTeam);
  const userProvider = useAppSelector((state) => state.user.provider);
  const userType = useAppSelector((state) => state.user.type);
  const userName = useAppSelector((state) => state.user.name);
  const userEmail = useAppSelector((state) => state.user.email);
  const firstCheckDone = useAppSelector((state) => state.user.firstCheckDone);

  const mediaQueryContext = useInitMediaQueryContext();

  React.useEffect(() => {
    dispatch(initializeUserProvider());
    dispatch(initializeTheme());
    dispatch(initializeUiData());
    dispatch(fetchDiscovery());

    setupSentry();
  }, []);

  React.useEffect(() => {
    if (!discoveryRequested || discoveryError) {
      return;
    }

    // Discovery is valid at this point

    // Check if request is coming from admin panel
    if (window.location.hash.includes("#type=")) {
      const data = Object.fromEntries(
        window.location.hash
          .slice(1)
          .split("&")
          .map((x) => x.split("=")),
      );
      if (data.type === "org") {
        dispatch(
          setUser({
            user: {
              email: data.email || "",
              name: data.name || "",
              photoUrl: "",
              uid: "",
            },
            extra: {
              type: "inspect",
              expires: data.expires,
              key: data.key,
              org: data.org,
            },
          }),
        );
      } else if (data.type === "user") {
        dispatch(
          setUser({
            user: {
              email: data.email || "",
              name: data.name || "",
              photoUrl: "",
              uid: "",
            },
            extra: {
              type: "impersonate",
              expires: data.expires,
              key: data.key,
              org: data.org,
            },
          }),
        );
      }
    } else {
      initializeFirebase();
    }
  }, [discoveryRequested, discoveryError]);

  React.useEffect(() => {
    setSentryUser();

    if (userType !== "none") {
      // TODO fails with inspect/impersonation, makes usage page unusable
      dispatch(fetchChargeableItems());
    }

    if (userType === "user") {
      if (IS_HUBSPOT_INTEGRATION_ENABLED && !isCplnTeam) {
        const time = new Date().toISOString().slice(0, 10);
        request({
          service: "self",
          method: "post",
          url: `${HUBSPOT_SERVICE_URL}/lastlogin`,
          body: { time, environment: DEPLOYMENT_ENV },
        }).catch(() => {
          console.error("Failure on handling crmId lastLogin");
        });
      }
    }
  }, [userType]);

  if (discoveryRequesting) {
    return (
      <MediaQueryContext.Provider value={mediaQueryContext}>
        <div
          className={`h-screen w-screen flex flex-col items-center justify-center text-normal`}
          style={{ backgroundColor: "var(--color-drop)" }}
        >
          <div className="z-10 h-auto w-40">
            <img
              className="px-4 py-3 object-contain"
              src={`/resources/logos/controlPlaneLogo${theme === "dark" ? "White" : ""}.svg`}
              alt={"Control Plane Logo"}
            />
          </div>
          <Spin />
          {IS_DEPLOYMENT_ENV_TEST ? <div className="mt-4 text-lg">Requesting Discovery Endpoint</div> : null}
        </div>
      </MediaQueryContext.Provider>
    );
  }

  if (discoveryError) {
    return (
      <MediaQueryContext.Provider value={mediaQueryContext}>
        <div
          className={`h-screen w-screen flex flex-col items-center justify-center text-normal`}
          style={{ backgroundColor: "var(--color-drop)" }}
        >
          <div className="z-10 h-auto w-40">
            <img
              className="px-4 py-3 object-contain"
              src={`/resources/logos/controlPlaneLogo${theme === "dark" ? "White" : ""}.svg`}
              alt={"Control Plane Logo"}
            />
          </div>
          <div className="px-4 py-2">Failed to connect to server. Please try again.</div>
          <div className="px-4 py-2">
            This is an issue limited to API service and UI, most likely your workloads are not affected.
          </div>
          <div className="px-4 py-2 flex gap-1">
            See the{" "}
            <ExternalLink
              label="status page"
              url={`https://status.${IS_DEPLOYMENT_ENV_TEST ? "test." : ""}cpln.io`}
              disableCopy
            />{" "}
            for details.
          </div>
          {IS_DEPLOYMENT_ENV_TEST ? <div className="mt-4 text-lg">{discoveryError}</div> : null}
        </div>
      </MediaQueryContext.Provider>
    );
  }

  if (!firstCheckDone) {
    return (
      <MediaQueryContext.Provider value={mediaQueryContext}>
        <div
          className={`h-screen w-screen flex flex-col items-center justify-center text-normal`}
          style={{ backgroundColor: "var(--color-drop)" }}
        >
          <div className="z-10 h-auto w-40">
            <img
              className="px-4 py-3 object-contain"
              src={`/resources/logos/controlPlaneLogo${theme === "dark" ? "White" : ""}.svg`}
              alt={"Control Plane Logo"}
            />
          </div>
          <Spin />
          {IS_DEPLOYMENT_ENV_TEST ? <div className="mt-4 text-lg">Making First Login Check</div> : null}
        </div>
      </MediaQueryContext.Provider>
    );
  }

  if (userProvider === "aws") {
    return <MarketplaceProviderAWS />;
  }

  if (!isLoggedIn) {
    const noUserRouter = createBrowserRouter(
      createRoutesFromElements(
        <Route
          element={
            <>
              <DocumentTitle />
              <Outlet />
            </>
          }
        >
          <Route path={"/signup/id/:id"} element={<SignUpWithId key={"signupWithId"} />} />
          <Route path={"/signup"} element={<SignIn key={"signup"} />} />
          <Route path={"*"} element={<SignIn key={"signin"} />} />
        </Route>,
      ),
    );

    return (
      <MediaQueryContext.Provider value={mediaQueryContext}>
        <RouterProvider router={noUserRouter} />
      </MediaQueryContext.Provider>
    );
  }

  if (isProcessing) {
    return (
      <MediaQueryContext.Provider value={mediaQueryContext}>
        <div
          className={`h-screen w-screen flex flex-col items-center justify-center text-normal`}
          style={{ backgroundColor: "var(--color-drop)" }}
        >
          <div className="z-10 h-auto w-40">
            <img
              className="px-4 py-3 object-contain"
              src={`/resources/logos/controlPlaneLogo${theme === "dark" ? "White" : ""}.svg`}
              alt={"Control Plane Logo"}
            />
          </div>
          <Spin />
          {IS_DEPLOYMENT_ENV_TEST ? <div className="mt-4 text-lg">Logged In. Requesting initial user data.</div> : null}
        </div>
      </MediaQueryContext.Provider>
    );
  }

  async function setSentryUser() {
    if (!IS_SENTRY_ENABLED) {
      return;
    }

    if (!sentryMetadata.initialized) {
      setTimeout(setSentryUser, 100);
      return;
    }

    if (userType !== "user") {
      Sentry.setUser(null);
    } else {
      Sentry.setUser({ username: userName, email: userEmail });
    }
  }

  return (
    <div>
      <MediaQueryContext.Provider value={mediaQueryContext}>
        <ConfigProvider
          wave={{ disabled: true }}
          theme={{
            cssVar: true,
            hashed: false,
            token: {
              colorBgBase: "#fff",
              colorPrimary: "#1563ff",
              colorTextBase: "#2a2d31",
              colorBorderSecondary: "#d9d9d9",
              fontFamily: "proxima-nova",
              fontSize: 16,
            },
            components: {
              Card: {
                bodyPadding: 32,
              } as any,
              Button: {
                contentFontSizeSM: 13,
              },
            },
          }}
        >
          <App />
        </ConfigProvider>
      </MediaQueryContext.Provider>
    </div>
  );
};
